import { isFlowActive } from "./flow";
import { BOARD_SIZE } from "./helpers";
import { isRefillEnabled, type ProgressionSnapshot } from "./progression";
import type { BoardTile, FlowState, RefillState } from "./types";

export {
  REFILL_ENABLED_STARTING_ROUND,
  REFILL_ENABLED_STARTING_SCORE
} from "./progression";
export const REFILL_WINDOW_MS = 8000;

type SyncRefillStateOptions = {
  refill: RefillState;
  board: readonly BoardTile[];
  flow: FlowState;
  now: number;
  progression: ProgressionSnapshot;
  activatedByClear?: boolean;
};

export function createInitialRefillState(progression: ProgressionSnapshot): RefillState {
  return {
    active: false,
    expiresAt: null,
    timerVisible: isRefillEnabled(progression),
    remainingMs: REFILL_WINDOW_MS
  };
}

export function hasRefillSpace(board: readonly BoardTile[]): boolean {
  return board.length < BOARD_SIZE;
}

function pauseRefillCountdown(refill: RefillState, pausedAt: number): RefillState {
  if (refill.expiresAt === null) {
    return refill;
  }

  return {
    ...refill,
    expiresAt: null,
    remainingMs: Math.max(0, refill.expiresAt - pausedAt)
  };
}

export function syncRefillState({
  refill,
  board,
  flow,
  now,
  progression,
  activatedByClear = false
}: SyncRefillStateOptions): RefillState {
  const timerVisible = refill.timerVisible || isRefillEnabled(progression);
  let nextRefill =
    timerVisible === refill.timerVisible ? refill : { ...refill, timerVisible };

  if (!timerVisible) {
    return nextRefill;
  }

  if (activatedByClear && !nextRefill.active) {
    nextRefill = {
      ...nextRefill,
      active: true,
      remainingMs: REFILL_WINDOW_MS
    };
  }

  if (!nextRefill.active) {
    return nextRefill.expiresAt === null ? nextRefill : { ...nextRefill, expiresAt: null };
  }

  if (!hasRefillSpace(board)) {
    return {
      ...nextRefill,
      expiresAt: null,
      remainingMs: REFILL_WINDOW_MS
    };
  }

  if (isFlowActive(flow)) {
    return pauseRefillCountdown(nextRefill, now);
  }

  if (nextRefill.expiresAt !== null) {
    return nextRefill;
  }

  const durationMs = nextRefill.remainingMs > 0 ? nextRefill.remainingMs : REFILL_WINDOW_MS;

  return {
    ...nextRefill,
    expiresAt: now + durationMs,
    remainingMs: durationMs
  };
}
